/**
 * Fashion card v2 (Tagalys shops) - colour swatches under the card image,
 * in-place colour switching from Storefront API data, and the mobile
 * "Select size" sheet.
 *
 * Loaded with defer from layout/theme.liquid on useTagalys shops only, and a
 * no-op without window.wbFashionCardConfig (printed there). Works on every
 * card carrying .wb-prdct20-fashion--v2 + data-base-title, whichever way it
 * was rendered: snippets/product-item-fashion.liquid (first page),
 * assets/tagalys-storefront-cards.js (filter results / load more) or the Vue
 * search card (snippets/vue-templates.liquid). The markup it clones (swatch
 * chip, size sheet, sheet rows) is printed by
 * snippets/wb-card-swatch-templates.liquid - no HTML strings or copy here.
 *
 * Colour siblings: products tagged "color-sorting" that share the base title
 * of a "Name | Colour" title. Cards are filled as they come near the
 * viewport; the styles of one tick share a single Storefront request (an
 * alias per style), so the cards on screen get their colours from one small
 * request right after the script runs and the rest follow while scrolling.
 * Ordered by window.wbSortColorSiblings (assets/index.layout.js), sold-out
 * colours hidden, answers kept in sessionStorage for a few minutes. Until a
 * card's colours arrive it shows the markup's blurred placeholder chips. The
 * whole-shop index the hover quick-add in tagalys-collection-filter.js reads
 * (window.wbColorSiblingsIndex.get) is still here, built only on demand.
 *
 * Switching colour replaces the whole grid item with a card drawn by
 * window.wbTagalysCards.buildNode() from the sibling's Storefront data, so
 * badges, prices, root classes, data attributes, tracking and quick-add all
 * follow the one existing rule set. In the search overlay the hit is replaced
 * in the Vue results list instead (vuesearch.replaceHitByProductId).
 */
(function () {
  'use strict';

  var config = window.wbFashionCardConfig;
  if (!config || !window.WBStorefrontApi) {
    return;
  }

  var CARD_SELECTOR = '.wb-prdct20-fashion--v2[data-base-title]';
  var SEARCH_RESULTS_SELECTOR = '.wb-hdr20__search-result-wrapper';
  var INDEX_TTL = 5 * 60 * 1000;
  var text = config.text || {};

  // ---------------------------------------------------------------------
  // helpers

  function numericId(gid) {
    var match = /(\d+)\s*$/.exec(String(gid || ''));
    return match ? match[1] : '';
  }

  function splitTitle(title) {
    var parts = String(title || '').split('|');
    return { base: (parts[0] || '').trim(), color: (parts[1] || '').trim() };
  }

  function productUrl(handle) {
    return (config.shopUrl || '') + '/products/' + handle;
  }

  function isPhone() {
    return window.innerWidth <= 767 || (typeof window.isMobile === 'function' && window.isMobile());
  }

  function contextVariables(variables) {
    if (config.country) {
      variables.country = config.country;
    }
    if (/^[A-Z]{2}$/.test(config.language || '')) {
      variables.language = config.language;
    }
    return variables;
  }

  function cloneTemplate(id) {
    var template = document.getElementById(id);
    return template && template.content && template.content.firstElementChild
      ? template.content.firstElementChild.cloneNode(true)
      : null;
  }

  // snippets/fashion-line-label.liquid, for cards drawn in JS.
  function lineLabel(tags) {
    var list = tags || [];
    var lineTags = config.lineTags || [];
    var labels = config.lineLabels || {};
    var index;
    for (index = 0; index < lineTags.length; index += 1) {
      if (list.indexOf(lineTags[index]) !== -1) {
        return String(labels[lineTags[index]] || lineTags[index]).toUpperCase();
      }
    }
    return '';
  }

  // The stylesheet is in <head> on collection templates only (layout); other
  // pages (search overlay) get it the moment a v2 card shows up.
  function ensureCss() {
    if (!config.cssUrl || document.querySelector('link[href*="wb-card-swatches"]')) {
      return;
    }
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = config.cssUrl;
    document.head.appendChild(link);
  }

  // ---------------------------------------------------------------------
  // colour siblings per style, fetched in batches as cards near the viewport
  //
  // One Storefront request per batch of styles (an alias per base title, a
  // prefix search on the title restricted to the "color-sorting" tag, exact
  // base title kept client-side - as index.layout.js does for the PDP
  // swatches). The cards on screen get their colours from the first small
  // request right after the script runs; the rest follow as they scroll in.

  // variants: the sizes too, so the mobile "Select size" sheet opens from
  // this data at once instead of fetching the product on each tap.
  var SIBLINGS_FIELDS = 'fragment WBSibling on Product { id handle title tags availableForSale variants(first: 50) { nodes { id title sku availableForSale } } }';
  var SIBLINGS_PER_REQUEST = 12;
  var siblingsCache = {};
  var siblingsPending = {};
  var siblingsQueue = [];
  var siblingsTimer = null;

  function storedSiblings() {
    try {
      var data = JSON.parse(sessionStorage.getItem(storageKey() + ':styles2') || 'null');
      return data && data.time && Date.now() - data.time <= INDEX_TTL && data.styles ? data.styles : {};
    } catch (error) {
      return {};
    }
  }

  var storeSiblingsTimer = null;
  function storeSiblings() {
    clearTimeout(storeSiblingsTimer);
    storeSiblingsTimer = setTimeout(function () {
      try {
        var styles = {};
        Object.keys(siblingsCache).forEach(function (key) {
          styles[key] = siblingsCache[key];
        });
        sessionStorage.setItem(storageKey() + ':styles2', JSON.stringify({ time: Date.now(), styles: styles }));
      } catch (error) {
        // quota / private mode: refetched on the next page
      }
    }, 500);
  }

  function styleKey(baseTitle) {
    return String(baseTitle || '').trim().toLowerCase();
  }

  function sortSiblings(entries) {
    if (typeof window.transformPreviewLaunchTagAlgoliaSearchResponse === 'function') {
      entries = window.transformPreviewLaunchTagAlgoliaSearchResponse({ hits: entries }).hits;
    }
    return typeof window.wbSortColorSiblings === 'function' ? window.wbSortColorSiblings(entries) : entries;
  }

  function searchString(baseTitle) {
    return "tag:'color-sorting' AND title:'" + String(baseTitle).replace(/(['\\])/g, '\\$1') + "*'";
  }

  function fetchSiblingsBatch(titles) {
    var query = 'query WBColorSiblings($country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language) {';
    titles.forEach(function (title, index) {
      query += ' s' + index + ': products(first: 60, query: ' + JSON.stringify(searchString(title)) + ') { nodes { ...WBSibling } }';
    });
    query += ' } ' + SIBLINGS_FIELDS;
    return window.WBStorefrontApi.fetch(query, contextVariables({})).then(function (data) {
      titles.forEach(function (title, index) {
        var key = styleKey(title);
        var nodes = data && data['s' + index] && data['s' + index].nodes;
        var entries;
        if (!nodes) {
          // failed: answer empty now, ask again next time
          siblingsPending[key].resolve([]);
          delete siblingsPending[key];
          return;
        }
        entries = nodes.map(entryFromNode).filter(function (entry) {
          return entry && styleKey(entry.baseTitle) === key;
        });
        siblingsCache[key] = sortSiblings(entries);
        siblingsPending[key].resolve(siblingsCache[key]);
        delete siblingsPending[key];
      });
      storeSiblings();
    });
  }

  function flushSiblingsQueue() {
    var titles = siblingsQueue.splice(0);
    var index;
    siblingsTimer = null;
    for (index = 0; index < titles.length; index += SIBLINGS_PER_REQUEST) {
      fetchSiblingsBatch(titles.slice(index, index + SIBLINGS_PER_REQUEST)).catch(function (error) {
        console.error('Card swatches: colour siblings failed', error);
      });
    }
  }

  // All colours of a style, in swatch order (sold out last; filtered by the
  // callers). Requests from the same tick share one Storefront request.
  function allSiblingsFor(baseTitle) {
    var key = styleKey(baseTitle);
    var stored;
    if (!key) {
      return Promise.resolve([]);
    }
    if (siblingsCache[key]) {
      return Promise.resolve(siblingsCache[key]);
    }
    stored = storedSiblings()[key];
    if (stored) {
      siblingsCache[key] = stored;
      return Promise.resolve(stored);
    }
    if (!siblingsPending[key]) {
      siblingsPending[key] = {};
      siblingsPending[key].promise = new Promise(function (resolve) {
        siblingsPending[key].resolve = resolve;
      });
      siblingsQueue.push(String(baseTitle).trim());
      if (!siblingsTimer) {
        siblingsTimer = setTimeout(flushSiblingsQueue, 30);
      }
    }
    return siblingsPending[key].promise;
  }

  // ---------------------------------------------------------------------
  // whole-shop colour index (compatibility: the hover quick-add in
  // tagalys-collection-filter.js reads window.wbColorSiblingsIndex.get();
  // built only on demand, the cards above use the per-style batches)

  var INDEX_QUERY = 'query WBColorSiblingIndex($handle: String!, $filters: [ProductFilter!], $cursor: String, $country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language) {' +
    ' collection(handle: $handle) {' +
    '  products(first: 250, after: $cursor, filters: $filters) {' +
    '   pageInfo { hasNextPage endCursor }' +
    '   nodes { id handle title tags availableForSale variants(first: 50) { nodes { availableForSale } } }' +
    '  }' +
    ' }' +
    '}';
  var indexPromise = null;

  function storageKey() {
    return 'wb:colorIndex:' + (config.country || '') + ':' + (config.language || '');
  }

  function readStoredEntries() {
    try {
      var data = JSON.parse(sessionStorage.getItem(storageKey()) || 'null');
      if (!data || !Array.isArray(data.entries) || Date.now() - data.time > INDEX_TTL) {
        return null;
      }
      return data.entries;
    } catch (error) {
      return null;
    }
  }

  function storeEntries(entries) {
    try {
      sessionStorage.setItem(storageKey(), JSON.stringify({ time: Date.now(), entries: entries }));
    } catch (error) {
      // quota / private mode: the index is simply rebuilt on the next page
    }
  }

  function entryFromNode(node) {
    var parts = splitTitle(node && node.title);
    if (!node || !parts.base || !parts.color) {
      return null;
    }
    return {
      gid: node.id,
      id: numericId(node.id),
      handle: node.handle,
      title: node.title,
      baseTitle: parts.base,
      color: parts.color,
      available: node.availableForSale !== false,
      available_sizes: ((node.variants && node.variants.nodes) || []).filter(function (variant) {
        return variant.availableForSale;
      }).length,
      variants: ((node.variants && node.variants.nodes) || []).map(function (variant) {
        return { id: numericId(variant.id), title: variant.title, sku: variant.sku || '', available: variant.availableForSale !== false };
      }),
      tags: node.tags || []
    };
  }

  function fetchIndexEntries(cursor, accumulated) {
    accumulated = accumulated || [];
    return window.WBStorefrontApi.fetch(INDEX_QUERY, contextVariables({
      handle: 'all',
      filters: [{ tag: 'color-sorting' }],
      cursor: cursor || null
    })).then(function (data) {
      var products = data && data.collection && data.collection.products;
      if (!products) {
        return accumulated.length ? accumulated : null;
      }
      (products.nodes || []).forEach(function (node) {
        var entry = entryFromNode(node);
        if (entry) {
          accumulated.push(entry);
        }
      });
      if (products.pageInfo && products.pageInfo.hasNextPage && products.pageInfo.endCursor) {
        return fetchIndexEntries(products.pageInfo.endCursor, accumulated);
      }
      return accumulated;
    });
  }

  // { "<base title>": [entries in swatch order] } - keyed by the exact base
  // title, as the quick-add hover in tagalys-collection-filter.js expects.
  function buildIndex(entries) {
    var byTitle = {};
    entries.forEach(function (entry) {
      (byTitle[entry.baseTitle] = byTitle[entry.baseTitle] || []).push(entry);
    });
    Object.keys(byTitle).forEach(function (baseTitle) {
      // Same launch-preview holdback and colour order as the PDP swatches.
      byTitle[baseTitle] = sortSiblings(byTitle[baseTitle]);
    });
    return byTitle;
  }

  function getIndex() {
    var stored;
    if (indexPromise) {
      return indexPromise;
    }
    stored = readStoredEntries();
    if (stored) {
      indexPromise = Promise.resolve(buildIndex(stored));
      return indexPromise;
    }
    indexPromise = fetchIndexEntries().then(function (entries) {
      if (!entries) {
        // Failed: answer empty now, try again on the next request.
        indexPromise = null;
        return {};
      }
      storeEntries(entries);
      return buildIndex(entries);
    }).catch(function (error) {
      console.error('Card swatches: colour index failed', error);
      indexPromise = null;
      return {};
    });
    return indexPromise;
  }

  // The colours a card shows for a style: in stock only (sold-out colours are
  // hidden), in swatch order, the card's own colour included.
  function siblingsFor(baseTitle) {
    return allSiblingsFor(baseTitle).then(function (entries) {
      return entries.filter(function (entry) {
        return entry.available;
      });
    });
  }

  window.wbColorSiblingsIndex = { get: getIndex, siblingsFor: siblingsFor };

  // ---------------------------------------------------------------------
  // swatch rows

  function rowParts(card) {
    var row = card.querySelector('.wb-card-swatches');
    return row ? {
      row: row,
      strip: row.querySelector('.wb-card-swatches__strip'),
      more: row.querySelector('.wb-card-swatches__more'),
      status: row.querySelector('.wb-card-swatches__status')
    } : null;
  }

  // The arrows: "more" on the right until the strip is scrolled to its end,
  // "prev" over the left edge once it has been scrolled.
  function updateMore(row) {
    var strip = row.querySelector('.wb-card-swatches__strip');
    var more = row.querySelector('.wb-card-swatches__more');
    var prev = row.querySelector('.wb-card-swatches__prev');
    var overflow;
    var atEnd;
    if (!strip) {
      return;
    }
    overflow = strip.scrollWidth - strip.clientWidth > 1;
    atEnd = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 1;
    strip.classList.toggle('wb-card-swatches__strip--scrolls', overflow);
    if (more) {
      more.hidden = !overflow || atEnd;
    }
    if (prev) {
      prev.hidden = !overflow || strip.scrollLeft <= 1;
    }
  }

  // Chip order and scroll position a card hands to the card that replaces
  // it on a colour switch (keyed by style + the new handle), so switching
  // never reorders or scrolls the strip - only the page load shows the card's
  // own colour first.
  var carriedStrips = {};

  function carryStrip(card, newHandle) {
    var strip = card.querySelector('.wb-card-swatches__strip');
    if (!strip) {
      return;
    }
    carriedStrips[styleKey(card.getAttribute('data-base-title')) + '|' + newHandle] = {
      order: Array.prototype.map.call(strip.querySelectorAll('.wb-card-swatch[data-handle]'), function (chip) {
        return chip.getAttribute('data-handle');
      }),
      scrollLeft: strip.scrollLeft
    };
  }

  function takeCarriedStrip(card) {
    var key = styleKey(card.getAttribute('data-base-title')) + '|' + card.getAttribute('data-product-handle');
    var carried = carriedStrips[key];
    delete carriedStrips[key];
    return carried && carried.order.length ? carried : null;
  }

  // After a colour switch the new card's selected chip draws its frame with
  // an animation (is-settling, wb-card-swatches.css); the class is dropped
  // once it has run so later state changes transition normally.
  var SETTLE_MS = 400;

  function settleChip(chip) {
    if (!chip) {
      return;
    }
    chip.classList.add('is-settling');
    setTimeout(function () {
      chip.classList.remove('is-settling');
    }, SETTLE_MS);
  }

  function makeChip(entry, activeHandle) {
    var chip = cloneTemplate('wb-tpl-card-swatch');
    if (!chip) {
      return null;
    }
    chip.setAttribute('data-handle', entry.handle);
    chip.setAttribute('data-gid', entry.gid);
    chip.setAttribute('data-product-id', entry.id);
    chip.setAttribute('data-color', entry.color);
    chip.setAttribute('aria-label', entry.color);
    chip.setAttribute('aria-pressed', entry.handle === activeHandle ? 'true' : 'false');
    if (chip.firstElementChild) {
      chip.firstElementChild.setAttribute('data-bgcolor', entry.color);
    }
    return chip;
  }

  // The blurred placeholder chips the markup shows until the colours arrive.
  function removePlaceholders(strip) {
    Array.prototype.forEach.call(strip.querySelectorAll('.wb-card-swatch--placeholder'), function (placeholder) {
      placeholder.remove();
    });
  }

  function renderStrip(card, siblings) {
    var parts = rowParts(card);
    var handle = card.getAttribute('data-product-handle');
    var fragment;
    var carried;
    if (!parts || !parts.strip) {
      return;
    }
    // A style with no other colour keeps its server-rendered chip only.
    if (siblings.length > 1 && siblings.some(function (entry) { return entry.handle === handle; })) {
      carried = takeCarriedStrip(card);
      if (carried) {
        // after a colour switch: the order (and scroll position) of the card it replaces
        siblings = siblings.slice().sort(function (a, b) {
          var left = carried.order.indexOf(a.handle);
          var right = carried.order.indexOf(b.handle);
          return (left === -1 ? carried.order.length : left) - (right === -1 ? carried.order.length : right);
        });
      } else {
        // page load: the card's own colour first, so it is in view
        siblings = siblings.filter(function (entry) { return entry.handle === handle; })
          .concat(siblings.filter(function (entry) { return entry.handle !== handle; }));
      }
      fragment = document.createDocumentFragment();
      siblings.forEach(function (entry) {
        var chip = makeChip(entry, handle);
        if (chip) {
          fragment.appendChild(chip);
        }
      });
      parts.strip.innerHTML = '';
      parts.strip.appendChild(fragment);
      if (carried) {
        parts.strip.scrollLeft = carried.scrollLeft;
        settleChip(parts.strip.querySelector('.wb-card-swatch[aria-pressed="true"]'));
      }
    } else {
      removePlaceholders(parts.strip);
    }
    updateMore(parts.row);
  }

  function fillCard(card) {
    var state = card.getAttribute('data-swatches');
    if (state === 'loading' || state === 'done') {
      return Promise.resolve();
    }
    card.setAttribute('data-swatches', 'loading');
    return siblingsFor(card.getAttribute('data-base-title')).then(function (siblings) {
      if (!document.body.contains(card)) {
        card.removeAttribute('data-swatches');
        return;
      }
      renderStrip(card, siblings);
      card.setAttribute('data-swatches', 'done');
    });
  }

  var intersection = typeof window.IntersectionObserver === 'function'
    ? new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          intersection.unobserve(entry.target);
          fillCard(entry.target);
        }
      });
    }, { rootMargin: '600px 0px' })
    : null;

  // Only the big collection grid waits for a card to come near the viewport
  // (60+ cards); the handful of cards elsewhere - homepage sliders, search
  // results - fill right away, so their colours are there when scrolled to.
  function fillsLazily() {
    return !!document.querySelector('.wb-shop-grid');
  }

  // Cards this script has taken on. A slider's loop clone copies the
  // original's markup, state attribute included - cloned while the original
  // was still loading it would otherwise sit on its placeholders for good.
  var tracked = typeof WeakSet === 'function' ? new WeakSet() : null;

  // Safety net for pictures the lazy loader never registered (Swiper loop
  // clones, slides inserted after a colour switch, cards re-rendered late):
  // when such a card comes near the viewport the lazy loader is refreshed,
  // so the picture loads instead of staying blank.
  var lazyGuardTimer = null;

  function refreshLazyLoader() {
    clearTimeout(lazyGuardTimer);
    lazyGuardTimer = setTimeout(function () {
      if (window.lazyLoadInstance && typeof window.lazyLoadInstance.update === 'function') {
        window.lazyLoadInstance.update();
      }
    }, 80);
  }

  var lazyGuard = typeof window.IntersectionObserver === 'function'
    ? new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && entry.target.querySelector('img.lazy:not([data-ll-status])')) {
          refreshLazyLoader();
        }
      });
    }, { rootMargin: '300px 300px' })
    : null;

  function watchCard(card) {
    var state = card.getAttribute('data-swatches');
    if (tracked) {
      if (tracked.has(card)) {
        return;
      }
      tracked.add(card);
      if (lazyGuard) {
        lazyGuard.observe(card);
      }
      if (state && state !== 'done') {
        card.removeAttribute('data-swatches');
        state = null;
      }
    }
    if (state) {
      return;
    }
    ensureCss();
    if (intersection && fillsLazily() && !card.closest(SEARCH_RESULTS_SELECTOR)) {
      card.setAttribute('data-swatches', 'pending');
      intersection.observe(card);
    } else {
      fillCard(card);
    }
  }

  function scan(root) {
    if (!root || root.nodeType !== 1) {
      return;
    }
    if (root.matches && root.matches(CARD_SELECTOR)) {
      watchCard(root);
    }
    Array.prototype.forEach.call(root.querySelectorAll(CARD_SELECTOR), watchCard);
    scanNavLists(root);
  }

  // ---------------------------------------------------------------------
  // sibling product data

  // The same fields as CARD_QUERY in assets/tagalys-storefront-cards.js, so
  // buildNode() draws the card from this answer unchanged.
  var SIBLING_QUERY = 'query WBSiblingCard($ids: [ID!]!, $country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language) {' +
    ' nodes(ids: $ids) { ... on Product {' +
    '  id handle title tags availableForSale createdAt productType vendor' +
    '  images(first: 8) { nodes { url altText } }' +
    '  media(first: 2) { nodes { mediaContentType } }' +
    '  variants(first: 50) { nodes { id title sku availableForSale price { amount } compareAtPrice { amount } selectedOptions { name value } } }' +
    ' } }' +
    '}';
  var siblingCache = {};

  function fetchSibling(gid) {
    if (!siblingCache[gid]) {
      siblingCache[gid] = window.WBStorefrontApi.fetch(SIBLING_QUERY, contextVariables({ ids: [gid] })).then(function (data) {
        var product = data && data.nodes && data.nodes[0];
        if (!product) {
          delete siblingCache[gid];
          return null;
        }
        return product;
      }, function () {
        delete siblingCache[gid];
        return null;
      });
    }
    return siblingCache[gid];
  }

  function toNumber(value) {
    var number = parseFloat(value);
    return isNaN(number) ? 0 : number;
  }

  // A Storefront product as a search hit, the shape
  // assets/tagalys-search-adapter.js gives the Vue search card; the hover
  // image goes straight into VueHoverImageManager's cache.
  function toSearchHit(product) {
    var variants = (product.variants && product.variants.nodes) || [];
    var images = ((product.images && product.images.nodes) || []).map(function (image) { return image.url; });
    var media = (product.media && product.media.nodes) || [];
    var available = variants.filter(function (variant) { return variant.availableForSale; });
    var pool = available.length ? available : variants;
    var cheapest = pool.reduce(function (best, variant) {
      return !best || toNumber(variant.price && variant.price.amount) < toNumber(best.price && best.price.amount) ? variant : best;
    }, null);
    var compareMax = variants.reduce(function (max, variant) {
      return Math.max(max, variant.compareAtPrice ? toNumber(variant.compareAtPrice.amount) : 0);
    }, 0);
    var id = numericId(product.id);
    var manager = window.VueHoverImageManager;
    var offset;
    if (manager && manager.cache && typeof manager.getHoverImageOffset === 'function') {
      offset = manager.getHoverImageOffset(product.handle || '', product.tags || [], !!media[1] && media[1].mediaContentType === 'VIDEO');
      manager.cache.set(String(id), images[offset] || null);
    }
    return {
      id: String(id),
      objectID: cheapest ? String(numericId(cheapest.id)) : String(id),
      sku: (cheapest && cheapest.sku) || '',
      handle: product.handle || '',
      title: product.title || '',
      tags: product.tags || [],
      available: !!product.availableForSale,
      inventory_available: !!product.availableForSale,
      price: cheapest ? toNumber(cheapest.price && cheapest.price.amount) : 0,
      compare_at_price: cheapest && cheapest.compareAtPrice ? toNumber(cheapest.compareAtPrice.amount) : 0,
      variants_compare_at_price_max: compareMax,
      image: images[0] || '',
      product_image: images[0] || '',
      media: []
    };
  }

  // ---------------------------------------------------------------------
  // colour switching

  function announce(card, message) {
    var parts = rowParts(card);
    if (parts && parts.status) {
      parts.status.textContent = message;
    }
  }

  function refreshPlugins() {
    if (window.lazyLoadInstance && typeof window.lazyLoadInstance.update === 'function') {
      window.lazyLoadInstance.update();
    }
    if (window.productReviewsInstance && typeof window.productReviewsInstance.updateCollection === 'function') {
      window.productReviewsInstance.updateCollection();
    }
    if (window.equalHeightInstance && typeof window.equalHeightInstance.update === 'function') {
      window.equalHeightInstance.update();
    }
  }

  // Phones swipe through the card's images (assets/plp-mobile-swipe.js): give
  // the swapped card its images, the one on screen first.
  function seedMobileSwipe(item, product) {
    var swipe = window.PLPMobileSwipe;
    var cards = window.wbTagalysCards;
    var images = ((product.images && product.images.nodes) || []).map(function (image) { return image.url; });
    var shownIndex = cards && typeof cards.shownImageIndex === 'function' ? cards.shownImageIndex(product) : 0;
    var shown = images[shownIndex];
    if (!swipe || !swipe.imageCache || !isPhone() || !shown) {
      return;
    }
    swipe.imageCache.set(String(numericId(product.id)), {
      allImages: [shown].concat(images.filter(function (url, index) { return index !== shownIndex; }))
    });
    if (typeof swipe.processCards === 'function') {
      swipe.processCards([item]);
    }
  }

  // ---- image handoff: the old picture stays as it is; the new card is put
  // in only once the picture it will show has loaded, so nothing pops in.

  // The srcset the card's main <picture> would pick at this viewport (the
  // first <source> whose media query matches, as the browser does).
  function mainPictureSrcset(node) {
    var picture = node.querySelector('.wb-prdct20-fashion__image-wrapper picture');
    var sources = picture ? picture.querySelectorAll('source') : [];
    var img = picture && picture.querySelector('img');
    var index;
    var media;
    for (index = 0; index < sources.length; index += 1) {
      media = sources[index].getAttribute('media');
      if (!media || (window.matchMedia && window.matchMedia(media).matches)) {
        return sources[index].getAttribute('data-srcset') || sources[index].getAttribute('srcset') || '';
      }
    }
    return img ? (img.getAttribute('data-src') || img.getAttribute('src') || '') : '';
  }

  // Resolves once the srcset's chosen candidate is loaded (or failed, or
  // after a timeout so a slow image never blocks the switch).
  function preloadSrcset(srcset, timeout) {
    return new Promise(function (resolve) {
      var done = false;
      var image;
      function finish() {
        if (!done) {
          done = true;
          resolve();
        }
      }
      if (!srcset) {
        finish();
        return;
      }
      image = new Image();
      image.onload = finish;
      image.onerror = finish;
      image.srcset = srcset;
      if (typeof image.decode === 'function') {
        image.decode().then(finish, finish);
      }
      setTimeout(finish, timeout || 4000);
    });
  }

  // The new card's main picture paints from cache right away instead of
  // waiting for the lazy loader (the hover picture stays lazy).
  function activateMainPicture(node) {
    var picture = node.querySelector('.wb-prdct20-fashion__image-wrapper picture');
    var img = picture && picture.querySelector('img');
    if (!picture) {
      return;
    }
    Array.prototype.forEach.call(picture.querySelectorAll('source[data-srcset]'), function (source) {
      source.setAttribute('srcset', source.getAttribute('data-srcset'));
      source.removeAttribute('data-srcset');
    });
    if (img && img.getAttribute('data-src')) {
      img.setAttribute('src', img.getAttribute('data-src'));
      img.removeAttribute('data-src');
      img.classList.remove('lazy');
      img.classList.add('loaded');
    }
  }

  // The Vue search card's <source> table (snippets/vue-templates.liquid):
  // [min viewport width, image size].
  var VUE_PICTURE_SIZES = [
    [1900, '450x'], [1800, '440x'], [1700, '415x'], [1600, '390x'], [1500, '365x'], [1400, '350x'],
    [1300, '315x'], [1200, '425x'], [1100, '390x'], [992, '355x'], [768, '500x'], [700, '400x'],
    [650, '400x'], [600, '400x'], [550, '400x'], [500, '350x'], [450, '350x'], [400, '300x'],
    [370, '300x'], [0, '300x']
  ];

  function vueSrcset(image) {
    var entry;
    var index;
    if (!image || !window.Shopify || typeof window.Shopify.resizeImage !== 'function') {
      return '';
    }
    for (index = 0; index < VUE_PICTURE_SIZES.length; index += 1) {
      if (window.innerWidth >= VUE_PICTURE_SIZES[index][0]) {
        entry = VUE_PICTURE_SIZES[index];
        break;
      }
    }
    return window.Shopify.resizeImage(image, entry[1]) + ' 1x, ' + window.Shopify.resizeImage(image, entry[1] + '@2x') + ' 2x, ' + window.Shopify.resizeImage(image, entry[1] + '@3x') + ' 3x';
  }

  // A switch takes at least this long, so one that has everything cached
  // still shows the chip animation (pending dot, then the ring) rather
  // than a jump.
  var SWITCH_MIN_MS = 350;

  function delay(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  // Grid cards (.wb-shop-grid__item) are replaced whole; cards in other
  // containers (homepage recommendation sliders: .swiper-slide /
  // .product-index-item) swap only the card element inside their wrapper.
  function swapGridCard(card, product, viaKeyboard, notBefore) {
    var item = card.closest('.wb-shop-grid__item');
    var cards = window.wbTagalysCards;
    var node = cards && cards.ready() ? cards.buildNode(product) : null;
    if (!node) {
      window.location.href = productUrl(product.handle);
      return Promise.resolve();
    }
    return Promise.all([preloadSrcset(mainPictureSrcset(node)), notBefore || null]).then(function () {
      if (!document.body.contains(card)) {
        return;
      }
      activateMainPicture(node);
      if (item) {
        insertSwappedCard(item, node, product, viaKeyboard);
      } else {
        insertSwappedSliderCard(card, node, product, viaKeyboard);
      }
    });
  }

  // Slider / standalone card: the wrapper (with its click tracking) stays,
  // its tracking ids follow the new product.
  function insertSwappedSliderCard(card, node, product, viaKeyboard) {
    var newCard = node.querySelector(CARD_SELECTOR) || node.querySelector('.wb-prdct20-fashion');
    var wrapper = card.parentNode;
    var onclick = wrapper && wrapper.getAttribute('onclick');
    var variantIds = node.getAttribute('data-variant-ids');
    var chip;
    if (!newCard || !wrapper) {
      window.location.href = productUrl(product.handle);
      return;
    }
    carryWishlist(wishlistBox(card), wishlistBox(newCard));
    carryHostClasses(card, newCard);
    wrapper.replaceChild(newCard, card);
    // a Vue card (recommendation grids): its component removes the
    // replacement when it is destroyed (beforeDestroy, vue-init.liquid)
    card.__wbSwapped = newCard;
    if (tracked) {
      tracked.add(newCard);
    }
    if (onclick && variantIds && onclick.indexOf('algoliaClickTracking') !== -1) {
      wrapper.setAttribute('onclick', onclick.replace(/'[\d,]*'\s*\)\s*;?\s*$/, "'" + variantIds + "')"));
    }
    newCard.setAttribute('data-swatches', 'loading');
    siblingsFor(newCard.getAttribute('data-base-title')).then(function (siblings) {
      renderStrip(newCard, siblings);
      newCard.setAttribute('data-swatches', 'done');
      chip = newCard.querySelector('.wb-card-swatch[aria-pressed="true"]');
      if (chip && viaKeyboard && document.activeElement === document.body) {
        chip.focus({ preventScroll: true });
      }
    });
    announce(newCard, splitTitle(product.title).color);
    refreshPlugins();
    document.dispatchEvent(new CustomEvent('wb:cardswapped', {
      detail: { handle: product.handle, gid: product.id, fromHandle: card.getAttribute('data-product-handle'), node: newCard }
    }));
  }

  // Classes the host puts on the card root stay with the replacement: the
  // Vue recommendation list sizes its cards through `items` on the card
  // itself (vue-templates.liquid), which the grid builder's card lacks. The
  // old product's own handle class and transient states are not carried.
  function carryHostClasses(oldCard, newCard) {
    Array.prototype.forEach.call(oldCard.classList, function (name) {
      if (/^wb-prdct20-fashion__/.test(name) || /^is-/.test(name) || newCard.classList.contains(name)) {
        return;
      }
      newCard.classList.add(name);
    });
  }

  // The wishlist app injects its <wishlist-button-collection> into a card's
  // .btn--addtowishlist-collection box asynchronously, which flickered on a
  // colour switch. The box the old card already has moves over to the new
  // card instead, pointed at the new product (the element observes these
  // data attributes and re-renders - the PDP colour switch does the same).
  var WISHLIST_ATTRIBUTES = ['data-product-handle', 'data-product-id', 'data-variant-id'];

  function carryWishlist(oldBox, newBox) {
    var inner = oldBox && oldBox.querySelector('wishlist-button-collection');
    if (!oldBox || !newBox || !inner || !newBox.parentNode) {
      return;
    }
    WISHLIST_ATTRIBUTES.forEach(function (name) {
      var value = newBox.getAttribute(name);
      if (value !== null) {
        oldBox.setAttribute(name, value);
        inner.setAttribute(name, value);
      }
    });
    newBox.parentNode.replaceChild(oldBox, newBox);
  }

  function wishlistBox(root) {
    return root ? root.querySelector('.wb-card-swatches__wishlist .btn--addtowishlist-collection') : null;
  }

  function insertSwappedCard(item, node, product, viaKeyboard) {
    var newCard;
    var chip;
    carryWishlist(wishlistBox(item), wishlistBox(node));
    node.setAttribute('data-order', item.getAttribute('data-order') || node.getAttribute('data-order'));
    // The handle this grid slot was rendered with - load more must not add
    // that colour again as a second card of the same style.
    node.setAttribute('data-swapped-from', item.getAttribute('data-swapped-from') || item.getAttribute('data-product-handle'));
    item.parentNode.replaceChild(node, item);
    newCard = node.querySelector(CARD_SELECTOR);
    if (newCard) {
      if (tracked) {
        tracked.add(newCard);
      }
      newCard.setAttribute('data-swatches', 'loading');
      siblingsFor(newCard.getAttribute('data-base-title')).then(function (siblings) {
        renderStrip(newCard, siblings);
        newCard.setAttribute('data-swatches', 'done');
        chip = newCard.querySelector('.wb-card-swatch[aria-pressed="true"]');
        // keyboard users keep their place on the new card; after a mouse
        // click nothing is focused, so no focus ring joins the selected ring
        if (chip && viaKeyboard && document.activeElement === document.body) {
          chip.focus({ preventScroll: true });
        }
      });
      announce(newCard, splitTitle(product.title).color);
    }
    seedMobileSwipe(node, product);
    refreshPlugins();
    document.dispatchEvent(new CustomEvent('wb:cardswapped', {
      detail: { handle: product.handle, gid: product.id, fromHandle: item.getAttribute('data-product-handle'), node: node }
    }));
  }

  // The hit object the search overlay rendered this card from. Walks up from
  // the root element's instance because the card markup can be a child
  // component sharing its parent's root element; the prop is the very object
  // in vuesearch.results.hits, so it identifies the card even when another
  // card shows the same product.
  function searchHitOf(card, search) {
    var hits = search.results && search.results.hits;
    for (var vm = card.__vue__; vm && hits; vm = vm.$parent) {
      if (vm.product && hits.indexOf(vm.product) !== -1) {
        return vm.product;
      }
    }
    return null;
  }

  function swapSearchCard(card, product, notBefore) {
    var search = window.vuesearch;
    var hit;
    if (!search || typeof search.replaceHitByProductId !== 'function') {
      window.location.href = productUrl(product.handle);
      return Promise.resolve();
    }
    hit = toSearchHit(product);
    return Promise.all([preloadSrcset(vueSrcset(hit.image)), notBefore || null]).then(function () {
      // keep the old card's wishlist button for the card Vue mounts for the new hit
      var oldBox = wishlistBox(card);
      if (oldBox && oldBox.querySelector('wishlist-button-collection')) {
        oldBox.parentNode.removeChild(oldBox);
      } else {
        oldBox = null;
      }
      // The new card mounts where this one is. Remember the neighbour instead of
      // looking the new card up by handle: the chosen colour can already be on
      // another card, and a handle lookup returns the first of them.
      var wrapper = card.parentNode;
      var before = card.previousElementSibling;
      search.replaceHitByProductId(card.getAttribute('data-product-id'), hit, searchHitOf(card, search));
      if (oldBox) {
        setTimeout(function () {
          var newCard = before ? before.nextElementSibling : (wrapper && wrapper.firstElementChild);
          carryWishlist(oldBox, wishlistBox(newCard));
        }, 0);
      }
    });
  }

  // viaKeyboard: the click came from Enter/Space (event.detail === 0).
  function onSwatchClick(chip, viaKeyboard) {
    var card = chip.closest(CARD_SELECTOR);
    var handle = chip.getAttribute('data-handle');
    var gid = chip.getAttribute('data-gid');
    if (!card || !handle) {
      return;
    }
    // The selected colour again, or the server chip before the colours
    // arrived: nothing to switch to.
    if (chip.getAttribute('aria-pressed') === 'true' || !gid) {
      return;
    }
    if (card.getAttribute('data-swapping') === 'true') {
      return;
    }
    card.setAttribute('data-swapping', 'true');
    // the picture stays as it is until the new card, with its picture already
    // loaded, takes over
    card.classList.add('is-switching');
    // The strip answers right away: the clicked dot shrinks to its selected
    // size (and breathes while loading), the old ring collapses. The frame
    // around the new colour is drawn when the new card is in (is-settling,
    // renderStrip). If the switch does not happen, both states are restored.
    var strip = chip.parentNode;
    var previous = strip ? strip.querySelector('.wb-card-swatch[aria-pressed="true"]') : null;
    chip.classList.add('is-pending');
    if (previous && previous !== chip) {
      previous.setAttribute('aria-pressed', 'false');
    }
    if (strip) {
      strip.setAttribute('aria-busy', 'true');
    }
    function restore() {
      card.removeAttribute('data-swapping');
      card.classList.remove('is-switching');
      chip.classList.remove('is-pending');
      if (previous && previous !== chip && document.body.contains(previous)) {
        previous.setAttribute('aria-pressed', 'true');
      }
      if (strip) {
        strip.removeAttribute('aria-busy');
      }
    }
    var notBefore = delay(SWITCH_MIN_MS);
    fetchSibling(gid).then(function (product) {
      var parts;
      if (!product) {
        window.location.href = productUrl(handle);
        return;
      }
      if (!product.availableForSale) {
        // Sold out since the colours were fetched: drop the swatch.
        parts = rowParts(card);
        chip.remove();
        if (parts) {
          updateMore(parts.row);
        }
        return;
      }
      carryStrip(card, product.handle);
      return card.closest(SEARCH_RESULTS_SELECTOR) ? swapSearchCard(card, product, notBefore) : swapGridCard(card, product, viaKeyboard, notBefore);
    }).then(function () {
      // the old card is gone after a swap; this only matters on the other paths
      restore();
    }, function (error) {
      console.error('Card swatches: colour switch failed', error);
      restore();
    });
  }

  function scrollStrip(arrow, direction) {
    var strip = arrow.parentNode && arrow.parentNode.querySelector('.wb-card-swatches__strip');
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!strip) {
      return;
    }
    if (typeof strip.scrollBy === 'function') {
      strip.scrollBy({ left: direction * strip.clientWidth, behavior: reduced ? 'auto' : 'smooth' });
    } else {
      strip.scrollLeft += direction * strip.clientWidth;
    }
  }

  // Mouse drag scrolls the strip (touch scrolls it natively). A drag that
  // moved marks the strip so the click it ends with picks no colour.
  var drag = null;

  function endDrag() {
    var strip;
    if (!drag) {
      return;
    }
    strip = drag.strip;
    if (drag.moved) {
      strip.classList.remove('is-dragging');
      strip.setAttribute('data-dragged', 'true');
      // the click of this drag is dispatched before this timeout runs
      setTimeout(function () {
        strip.removeAttribute('data-dragged');
      }, 0);
      scheduleArrowUpdate(strip);
    }
    drag = null;
  }

  document.addEventListener('pointerdown', function (event) {
    var strip = event.target && typeof event.target.closest === 'function' ? event.target.closest('.wb-card-swatches__strip') : null;
    if (!strip || event.pointerType !== 'mouse' || event.button !== 0 || !strip.closest(CARD_SELECTOR)) {
      return;
    }
    if (strip.scrollWidth - strip.clientWidth <= 1) {
      return;
    }
    drag = { strip: strip, startX: event.clientX, startLeft: strip.scrollLeft, moved: false };
    // no text selection / native drag while dragging the strip
    event.preventDefault();
  });

  window.addEventListener('pointermove', function (event) {
    var delta;
    if (!drag) {
      return;
    }
    delta = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(delta) > 3) {
      drag.moved = true;
      drag.strip.classList.add('is-dragging');
    }
    if (drag.moved) {
      drag.strip.scrollLeft = drag.startLeft - delta;
    }
  });
  window.addEventListener('pointerup', endDrag);
  window.addEventListener('pointercancel', endDrag);

  // Capture phase, so a swatch click never reaches the card's own click
  // tracking (onclick on the grid item, @click on the Vue card).
  document.addEventListener('click', function (event) {
    var target = event.target;
    var strip;
    var arrow;
    var chip;
    if (!target || typeof target.closest !== 'function') {
      return;
    }
    strip = target.closest('.wb-card-swatches__strip');
    if (strip && strip.getAttribute('data-dragged') === 'true') {
      // the click that ends a drag
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    arrow = target.closest('.wb-card-swatches__more, .wb-card-swatches__prev');
    if (arrow && arrow.closest(CARD_SELECTOR)) {
      event.preventDefault();
      event.stopPropagation();
      scrollStrip(arrow, arrow.classList.contains('wb-card-swatches__prev') ? -1 : 1);
      return;
    }
    chip = target.closest('.wb-card-swatch');
    if (chip && chip.closest(CARD_SELECTOR)) {
      event.preventDefault();
      event.stopPropagation();
      onSwatchClick(chip, event.detail === 0);
    }
  }, true);

  function prefetchFromEvent(event) {
    var target = event.target && typeof event.target.closest === 'function' ? event.target : null;
    var chip = target && target.closest('.wb-card-swatch');
    var gid = chip && chip.getAttribute('data-gid');
    var toggle = target && target.closest('.wb-quick-add__toggle');
    var card = toggle && toggle.closest(CARD_SELECTOR);
    if (gid && chip.getAttribute('aria-pressed') !== 'true') {
      fetchSibling(gid);
    }
    if (card) {
      // the bag icon: the sheet is built from the colour data, have it ready
      allSiblingsFor(card.getAttribute('data-base-title'));
    }
  }
  document.addEventListener('mouseover', prefetchFromEvent);
  document.addEventListener('touchstart', prefetchFromEvent, { passive: true });

  // The arrows follow the strip's position once a scroll has settled (not
  // on every frame of a smooth scroll, where the left arrow would appear
  // before the right one is gone and the row would shift twice).
  var arrowTimers = typeof WeakMap === 'function' ? new WeakMap() : null;

  function scheduleArrowUpdate(strip) {
    var timer = arrowTimers && arrowTimers.get(strip);
    if (!strip.parentNode) {
      return;
    }
    if (!arrowTimers) {
      updateMore(strip.parentNode);
      return;
    }
    clearTimeout(timer);
    arrowTimers.set(strip, setTimeout(function () {
      if (strip.parentNode) {
        updateMore(strip.parentNode);
      }
    }, 120));
  }

  function isStrip(node) {
    return node && node.nodeType === 1 && node.classList.contains('wb-card-swatches__strip');
  }

  // (no scrollend: with scroll-snap it can fire between the programmatic
  // scroll and the snap, i.e. before the strip has really settled)
  document.addEventListener('scroll', function (event) {
    if (isStrip(event.target)) {
      scheduleArrowUpdate(event.target);
    }
  }, true);

  var resizeTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      // through the per-strip timer, so a strip still scrolling isn't updated mid-way
      Array.prototype.forEach.call(document.querySelectorAll(CARD_SELECTOR + ' .wb-card-swatches__strip'), scheduleArrowUpdate);
      if (!navResize) {
        navLists.forEach(scheduleNavUpdate);
      }
    }, 150);
  });

  // ---------------------------------------------------------------------
  // mobile "Select size" sheet

  var sheet = null;
  var sheetOpener = null;

  function fallbackToSidebar(toggle) {
    if (typeof window.sidebarTrigger === 'function') {
      window.sidebarTrigger(toggle);
    }
  }

  function fetchProductJson(handle) {
    return fetch('/products/' + handle + '.js').then(function (response) {
      return response.ok ? response.json() : null;
    }).then(function (product) {
      if (product && typeof window.transformAppAxiosProductResponse === 'function') {
        return Promise.resolve(window.transformAppAxiosProductResponse({ data: product })).then(function (result) {
          return result && result.data ? result.data : product;
        });
      }
      return product;
    });
  }

  var sheetClosing = false;

  // Slides the sheet down (CSS is-closing animation) before the dialog closes.
  function closeSheet() {
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var done = false;
    var timer;
    if (!sheet || !sheet.open || sheetClosing) {
      return;
    }
    function finish() {
      if (done) {
        return;
      }
      done = true;
      clearTimeout(timer);
      sheetClosing = false;
      sheet.classList.remove('is-closing');
      if (sheet.open) {
        sheet.close();
      }
    }
    if (reduced) {
      finish();
      return;
    }
    sheetClosing = true;
    sheet.classList.add('is-closing');
    sheet.addEventListener('animationend', function onEnd(event) {
      if (event.target === sheet) {
        sheet.removeEventListener('animationend', onEnd);
        finish();
      }
    });
    timer = setTimeout(finish, 400);
  }

  function getSheet() {
    if (sheet) {
      return sheet;
    }
    sheet = cloneTemplate('wb-tpl-size-sheet');
    if (!sheet) {
      return null;
    }
    document.body.appendChild(sheet);
    sheet.addEventListener('click', function (event) {
      // The dialog element itself is only hit on the backdrop.
      if (event.target === sheet || (event.target.closest && event.target.closest('.wb-size-sheet__close'))) {
        closeSheet();
      }
    });
    // Escape: close with the slide-down too
    sheet.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeSheet();
    });
    sheet.addEventListener('close', function () {
      document.documentElement.classList.remove('wb-size-sheet-open');
      if (sheetOpener && typeof sheetOpener.focus === 'function' && document.body.contains(sheetOpener)) {
        sheetOpener.focus({ preventScroll: true });
      }
      sheetOpener = null;
    });
    return sheet;
  }

  // One row of the sheet. label: the size (or length) shown; variant: the
  // variant it adds (or, sold out, the one the notify link preselects);
  // onPick: a step instead of an add (a size that still needs its length).
  function sheetRow(product, label, variant, available, onPick) {
    var image = String(product.featured_image || '').replace(/(\.(jpg|png)\?)/, '_x500$1');
    var row = cloneTemplate(available ? 'wb-tpl-size-row' : 'wb-tpl-size-row-soldout');
    var size = row && row.querySelector('.wb-size-sheet__size');
    if (!row) {
      return null;
    }
    if (size) {
      size.textContent = available ? label : label + (text.soldOut ? ' - ' + text.soldOut : '');
    }
    if (!available) {
      // The notify-me form lives on the product page; it preselects this
      // size from the hash (templates/product.fashion.liquid).
      row.setAttribute('href', productUrl(product.handle) + '#notify=' + encodeURIComponent(variant.sku || variant.id));
    } else if (onPick) {
      row.addEventListener('click', onPick);
    } else {
      row.addEventListener('click', function () {
        if (typeof window.onQuickAddClicked === 'function') {
          window.onQuickAddClicked(row, variant.id, product.handle, product.title + ';' + variant.title, image);
        }
      });
    }
    return row;
  }

  // "Size - Length" products (leggings): the sizes first, then the lengths of
  // the picked size, as the hover quick-add does (quickAddRenderStructuredVariants).
  function sheetSizeGroups(product) {
    var groups = [];
    var bySize = {};
    product.variants.forEach(function (variant) {
      var segments = String(variant.title).split(' - ');
      var size = segments[0];
      if (!bySize[size]) {
        bySize[size] = { size: size, variants: [], available: false };
        groups.push(bySize[size]);
      }
      bySize[size].variants.push({ variant: variant, length: segments.slice(1).join(' - ') });
      if (variant.available) {
        bySize[size].available = true;
      }
    });
    return groups;
  }

  // Replaces the sheet's rows, easing the list's height from the old to the
  // new set (the sheet is bottom-anchored, so it shrinks or grows smoothly
  // instead of jumping). No easing on the first fill or with reduced motion.
  function setSheetRows(list, fragment) {
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var from = list.offsetHeight;
    var to;
    var timer;
    function settle() {
      clearTimeout(timer);
      list.removeEventListener('transitionend', settle);
      list.style.height = '';
      list.style.overflow = '';
      list.style.transition = '';
    }
    if (reduced || !from) {
      list.innerHTML = '';
      list.appendChild(fragment);
      return;
    }
    list.style.transition = 'none';
    list.style.overflow = 'hidden';
    list.innerHTML = '';
    list.appendChild(fragment);
    // the new natural height, then lock the old one and ease between them
    list.style.height = '';
    to = list.offsetHeight;
    if (to === from) {
      settle();
      return;
    }
    list.style.height = from + 'px';
    list.offsetHeight; // commit the start height before the transition
    window.requestAnimationFrame(function () {
      list.style.transition = 'height 260ms cubic-bezier(0.2, 0.8, 0.2, 1)';
      list.style.height = to + 'px';
      list.addEventListener('transitionend', settle);
      timer = setTimeout(settle, 400);
    });
  }

  function renderSheetList(list, product) {
    var structured = String(product.variants[0].title).indexOf(' - ') !== -1;
    var fragment = document.createDocumentFragment();
    function append(row) {
      if (row) {
        fragment.appendChild(row);
      }
    }
    if (!structured) {
      product.variants.forEach(function (variant) {
        append(sheetRow(product, variant.title, variant, !!variant.available));
      });
    } else {
      sheetSizeGroups(product).forEach(function (group) {
        append(sheetRow(product, group.size, group.variants[0].variant, group.available, function () {
          renderSheetLengths(list, product, group);
        }));
      });
    }
    setSheetRows(list, fragment);
  }

  function renderSheetLengths(list, product, group) {
    var fragment = document.createDocumentFragment();
    var back = cloneTemplate('wb-tpl-size-row');
    var label = back && back.querySelector('.wb-size-sheet__size');
    if (back) {
      back.classList.add('wb-size-sheet__row--back');
      if (label) {
        label.textContent = group.size;
      }
      back.addEventListener('click', function () {
        renderSheetList(list, product);
      });
      fragment.appendChild(back);
    }
    group.variants.forEach(function (entry) {
      var row = sheetRow(product, entry.length || entry.variant.title, entry.variant, !!entry.variant.available);
      if (row) {
        fragment.appendChild(row);
      }
    });
    setSheetRows(list, fragment);
  }

  function openSheet(toggle, product) {
    var dialog = getSheet();
    var list = dialog && dialog.querySelector('.wb-size-sheet__list');
    var context = toggle.closest('[data-gtm-context]');
    if (!dialog || !list) {
      fallbackToSidebar(toggle);
      return;
    }
    renderSheetList(list, product);
    if (context) {
      list.setAttribute('data-gtm-context', context.getAttribute('data-gtm-context'));
    } else {
      list.removeAttribute('data-gtm-context');
    }
    sheetOpener = toggle;
    sheetClosing = false;
    dialog.classList.remove('is-closing');
    document.documentElement.classList.add('wb-size-sheet-open');
    dialog.showModal();
  }

  // The sheet's product from the card's colour data (sizes included), with
  // the card's own title and picture - no request needed.
  function sheetProductFromCard(toggle, handle) {
    var card = toggle.closest(CARD_SELECTOR);
    var img = card && card.querySelector('.wb-prdct20-fashion__image-wrapper img');
    if (!card) {
      return Promise.resolve(null);
    }
    return allSiblingsFor(card.getAttribute('data-base-title')).then(function (entries) {
      var entry = entries.filter(function (item) { return item.handle === handle; })[0];
      if (!entry || !entry.variants || !entry.variants.length) {
        return null;
      }
      return {
        handle: entry.handle,
        title: entry.title,
        tags: entry.tags,
        variants: entry.variants,
        featured_image: img ? (img.currentSrc || img.getAttribute('src') || img.getAttribute('data-src') || '') : '',
        options: ['Size']
      };
    });
  }

  var sheetOpening = false;

  // Bound to the card's bag icon (data-handle) by the card markup; the
  // quick-add sidebar stays the answer for anything the sheet can't show:
  // bundles and colour/flavour variants. One tap at a time: taps while the
  // sheet is opening (or open) are ignored.
  window.wbCardSizeSheetOpen = function (toggle) {
    var handle = toggle && toggle.getAttribute('data-handle');
    if (!handle || sheetOpening || (sheet && sheet.open)) {
      return;
    }
    if (typeof window.HTMLDialogElement !== 'function' || !document.getElementById('wb-tpl-size-sheet')) {
      fallbackToSidebar(toggle);
      return;
    }
    sheetOpening = true;
    sheetProductFromCard(toggle, handle).then(function (fromCard) {
      if (fromCard && (fromCard.tags || []).indexOf('bundle') === -1) {
        return fromCard;
      }
      return fetchProductJson(handle);
    }).then(function (product) {
      sheetOpening = false;
      var display = product && typeof window.wbVariantDisplay === 'function' ? window.wbVariantDisplay(product) : {};
      if (!product || !product.variants || !product.variants.length ||
        (product.tags || []).indexOf('bundle') !== -1 ||
        display.colorSwatches || display.label === 'flavors') {
        fallbackToSidebar(toggle);
        return;
      }
      openSheet(toggle, product);
    }).catch(function () {
      sheetOpening = false;
      fallbackToSidebar(toggle);
    });
  };

  document.addEventListener('wb:addtocartsuccess', function (event) {
    if (sheet && sheet.open && event.detail && event.detail.quickadd) {
      // leave the success check visible for a moment
      setTimeout(closeSheet, 700);
    }
  });

  // ---------------------------------------------------------------------
  // arrows of the PDP tab lists (snippets/tagalys_tabs.liquid renders them
  // on Tagalys shops): the lists scroll as one row in the v2 design and
  // these arrows page through them on desktop

  var NAV_LIST_SELECTOR = '.product-index-wrapper .side--cart---algolia-recom--wrap';
  var NAV_GAP = 14;
  var navLists = [];
  var navTracked = typeof WeakSet === 'function' ? new WeakSet() : null;
  var navFrames = typeof WeakMap === 'function' ? new WeakMap() : null;

  function navOf(list) {
    var wrapper = list.closest ? list.closest('.product-index-wrapper') : null;
    return wrapper ? wrapper.querySelector('.wb-recom-tabs__nav') : null;
  }

  function updateNav(list) {
    var nav = navOf(list);
    if (!nav) {
      return;
    }
    var max = list.scrollWidth - list.clientWidth;
    var prev = nav.querySelector('.wb-recom-tabs__nav-prev');
    var next = nav.querySelector('.wb-recom-tabs__nav-next');
    if (prev) {
      prev.classList.toggle('wb-product-slider__nav--disabled', max < 2 || list.scrollLeft < 2);
    }
    if (next) {
      next.classList.toggle('wb-product-slider__nav--disabled', max < 2 || list.scrollLeft > max - 2);
    }
  }

  function scheduleNavUpdate(list) {
    if (!navFrames) {
      updateNav(list);
      return;
    }
    if (navFrames.get(list)) {
      return;
    }
    navFrames.set(list, requestAnimationFrame(function () {
      navFrames.delete(list);
      updateNav(list);
    }));
  }

  // one page = as many whole cards as are visible
  function pageList(list, direction) {
    var item = list.querySelector('.items');
    var step = item ? item.getBoundingClientRect().width + NAV_GAP : list.clientWidth / 2;
    var visible = list.clientWidth - (parseFloat(getComputedStyle(list).paddingRight) || 0);
    var count = Math.max(1, Math.floor((visible + 1) / step));
    var left = list.scrollLeft + direction * count * step;
    if (typeof list.scrollTo === 'function') {
      list.scrollTo({ left: left, behavior: 'smooth' });
    } else {
      list.scrollLeft = left;
    }
  }

  // a list changes size when its tab is shown, when its cards render and on
  // window resize; each of these needs the arrows re-evaluated
  var navResize = typeof window.ResizeObserver === 'function'
    ? new ResizeObserver(function (entries) {
      entries.forEach(function (entry) {
        scheduleNavUpdate(entry.target);
      });
    })
    : null;

  function watchNavList(list) {
    if (navTracked) {
      if (navTracked.has(list)) {
        return;
      }
      navTracked.add(list);
    } else if (navLists.indexOf(list) !== -1) {
      return;
    }
    navLists.push(list);
    if (navResize) {
      navResize.observe(list);
    }
    scheduleNavUpdate(list);
  }

  function scanNavLists(root) {
    if (!root.querySelectorAll) {
      return;
    }
    if (root.matches && root.matches(NAV_LIST_SELECTOR)) {
      watchNavList(root);
    }
    Array.prototype.forEach.call(root.querySelectorAll(NAV_LIST_SELECTOR), watchNavList);
    var list = root.closest ? root.closest(NAV_LIST_SELECTOR) : null;
    if (list) {
      scheduleNavUpdate(list);
    }
  }

  document.addEventListener('click', function (event) {
    var arrow = event.target && event.target.closest ? event.target.closest('.wb-recom-tabs__nav a') : null;
    if (!arrow) {
      return;
    }
    event.preventDefault();
    var wrapper = arrow.closest('.product-index-wrapper');
    var list = wrapper ? wrapper.querySelector('.side--cart---algolia-recom--wrap') : null;
    if (list) {
      pageList(list, arrow.classList.contains('wb-recom-tabs__nav-prev') ? -1 : 1);
    }
  });

  document.addEventListener('scroll', function (event) {
    var target = event.target;
    if (target && target.nodeType === 1 && target.matches && target.matches(NAV_LIST_SELECTOR)) {
      scheduleNavUpdate(target);
    }
  }, true);

  // ---------------------------------------------------------------------
  // PDP colour swatches (snippets/fashion-color-variant.liquid, switched in
  // place by assets/pdp-color-switch.js): the same click animation as the
  // card chips. The rail keeps its elements across a switch, so the clicked
  // item is pending until pdp-color-switch.js marks it active and fires
  // wb:colorswitch; then its frame is drawn with the settle animation.

  var PDP_SWATCH_SELECTOR = '#fashion-color-switch .sidebar--color-item, #fashion-color-limited-switch .sidebar--color-item';
  // The cart's "Edit selection" sidebar (quickadd-sidebar.liquid) draws the
  // same rail with a "-sidebar" suffix on the ids. Its colour click fetches
  // the product and then either moves the active class (same style) or
  // rebuilds the rail (other style): a short-lived observer on the rail
  // settles whichever element ends up active.
  var SIDEBAR_SWATCH_SELECTOR = '#fashion-color-switch-sidebar .sidebar--color-item, #fashion-color-limited-switch-sidebar .sidebar--color-item';
  var SIDEBAR_SWITCH_TIMEOUT_MS = 15000;

  function watchSidebarSwitch(rail, pending, previous) {
    if (typeof window.MutationObserver !== 'function') {
      return;
    }
    var done = false;
    var timer;
    function finish(active) {
      if (done) {
        return;
      }
      done = true;
      clearTimeout(timer);
      observer.disconnect();
      Array.prototype.forEach.call(rail.querySelectorAll('.sidebar--color-item.is-pending'), function (item) {
        item.classList.remove('is-pending');
      });
      if (active) {
        settleChip(active);
      } else if (previous && rail.contains(previous) && !rail.querySelector('.sidebar--color-item.active')) {
        previous.classList.add('active');
      }
    }
    var observer = new MutationObserver(function () {
      var active = rail.querySelector('.sidebar--color-item.active');
      if (active && active !== previous) {
        finish(active);
      }
    });
    observer.observe(rail, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
    timer = setTimeout(function () { finish(null); }, SIDEBAR_SWITCH_TIMEOUT_MS);
  }

  // Capture at window: before pdp-color-switch.js's own document-level
  // capture listener, whatever it does with propagation. Only classes are
  // touched here, the click itself is left to that script / the sidebar.
  window.addEventListener('click', function (event) {
    var item = event.target && event.target.closest ? event.target.closest(PDP_SWATCH_SELECTOR + ', ' + SIDEBAR_SWATCH_SELECTOR) : null;
    if (!item || event.defaultPrevented) {
      return;
    }
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || (typeof event.button === 'number' && event.button !== 0)) {
      return;
    }
    var inSidebar = !!item.closest('#sidebar');
    if (item.classList.contains('active') || (!inSidebar && !document.querySelector('#wb-fashion'))) {
      return;
    }
    var rail = item.closest('.wb-fashion-color-switch');
    var previous = rail ? rail.querySelector('.sidebar--color-item.active') : null;
    item.classList.add('is-pending');
    if (rail) {
      Array.prototype.forEach.call(rail.querySelectorAll('.sidebar--color-item.active'), function (active) {
        active.classList.remove('active');
      });
    }
    if (inSidebar && rail) {
      watchSidebarSwitch(rail, item, previous);
    }
  }, true);

  document.addEventListener('wb:colorswitch', function () {
    Array.prototype.forEach.call(document.querySelectorAll(PDP_SWATCH_SELECTOR), function (item) {
      if (!item.classList.contains('active')) {
        return;
      }
      if (item.classList.contains('is-pending')) {
        item.classList.remove('is-pending');
        settleChip(item);
      }
    });
  });

  // ---------------------------------------------------------------------
  // boot

  // The quick-add hover panel (tagalys-collection-helpers.liquid's wbItemHover,
  // tagalys-collection-filter.js) fetches /products/<handle>.js on the first
  // hover; the sibling cache already holds every card's variants, so a
  // product in that shape is handed over synchronously and the sizes render
  // on the hover itself. null when the style's siblings aren't loaded yet.
  function cachedProduct(handle, element) {
    var entry = null;
    var card;
    var img;
    if (!handle) {
      return null;
    }
    Object.keys(siblingsCache).some(function (key) {
      entry = siblingsCache[key].filter(function (item) { return item.handle === handle; })[0] || null;
      return !!entry;
    });
    if (!entry || !entry.variants || !entry.variants.length || (entry.tags || []).indexOf('bundle') !== -1) {
      return null;
    }
    card = (element && element.closest && element.closest('.wb-prdct20-item')) || element ||
      document.querySelector('.wb-prdct20-fashion[data-product-handle="' + handle.replace(/"/g, '\\"') + '"]');
    img = card && card.querySelector ? card.querySelector('.wb-prdct20-fashion__image-wrapper img, img') : null;
    return {
      id: entry.id,
      handle: entry.handle,
      title: entry.title,
      tags: entry.tags || [],
      available: entry.available,
      options: ['Size'],
      featured_image: img ? (img.currentSrc || img.getAttribute('src') || img.getAttribute('data-src') || '') : '',
      variants: entry.variants.map(function (variant) {
        return {
          id: variant.id,
          title: variant.title,
          option1: variant.title,
          option2: null,
          option3: null,
          name: entry.title + ' - ' + variant.title,
          sku: variant.sku,
          available: variant.available,
          price: null,
          compare_at_price: null,
          featured_image: null
        };
      })
    };
  }

  window.wbCardSwatches = {
    cachedProduct: cachedProduct,
    fillCard: fillCard,
    swapGridCard: swapGridCard,
    fetchSibling: fetchSibling,
    toSearchHit: toSearchHit,
    siblingsFor: siblingsFor,
    lineLabel: lineLabel,
    ensureCss: ensureCss,
    scan: scan
  };

  scan(document.body);

  if (typeof window.MutationObserver === 'function') {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.addedNodes, scan);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

})();
